import { t } from 'i18next';
import { z } from 'zod';
import { OnboardingFormFields } from '@Types/index';
import type { Country } from '@Types/country';

interface StateRefineData {
  [OnboardingFormFields.COUNTRY]?: Country | null;
  [OnboardingFormFields.STATE]?: { title: string; value: string } | null;
}

export const AddressStateSchema = z.object({
  // Validate as a single value so the error attaches to the STATE field itself
  // (rendered under the dropdown) rather than to nested title/value paths.
  [OnboardingFormFields.STATE]: z
    .object({ title: z.string(), value: z.string() })
    .nullable()
    .superRefine((value, ctx) => {
      if (!value?.title?.trim() || !value?.value?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('enErrors:required'),
        });
      }
    }),
});

export const addressStateNonUs = z.object({
  [OnboardingFormFields.STATE]: z
    .object(
      { title: z.string(), value: z.string() },
      {
        error: t('enErrors:required'),
      }
    )
    .nullable()
    .optional(),
});

const getStateValidations = (country: Country) => {
  if (country.subDivisions?.length > 1) {
    return z
      .object(
        { title: z.string(), value: z.string().min(1, t('enErrors:required')) },
        {
          error: t('enErrors:required'),
        }
      )
      .required();
  } else {
    return null;
  }
};

export const nonUsRefineState = <T extends StateRefineData>(schema: z.ZodType<T>) =>
  schema.superRefine((data, ctx) => {
    if (data[OnboardingFormFields.COUNTRY]) {
      const stateSchemaValidation = getStateValidations(data[OnboardingFormFields.COUNTRY]);
      if (stateSchemaValidation) {
        const stateResult = stateSchemaValidation.safeParse(data[OnboardingFormFields.STATE]);

        if (!stateResult.success) {
          stateResult.error.issues.forEach((issue) => {
            ctx.addIssue({
              code: 'custom',
              message: issue.message,
              path: [OnboardingFormFields.STATE],
            });
          });
        }
      }
    }
  });
